import { useEffect, useRef, useState } from 'react';

interface Props {
  onCapture: (dataUrl: string) => void;
  onClose: () => void;
}

declare global {
  interface Window { cv: any; jscanify: any; _cvLoading?: boolean; }
}

type Pt = { x: number; y: number };
type Quad = { tl: Pt; tr: Pt; br: Pt; bl: Pt };

function appHsl(name: string, alpha?: number) {
  const value = getComputedStyle(document.documentElement).getPropertyValue(name).trim();
  if (!value) return alpha === undefined ? 'hsl(155 79% 19%)' : `hsl(155 79% 19% / ${alpha})`;
  return alpha === undefined ? `hsl(${value})` : `hsl(${value} / ${alpha})`;
}

// ─── Lib loading ──────────────────────────────────────────────────────────────

async function loadScannerLibs(): Promise<any> {
  if (!window.cv?.Mat) {
    await new Promise<void>((resolve, reject) => {
      if (window._cvLoading) {
        const check = setInterval(() => { if (window.cv?.Mat) { clearInterval(check); resolve(); } }, 200);
        setTimeout(() => { clearInterval(check); reject(new Error('cv timeout')); }, 20000);
        return;
      }
      window._cvLoading = true;
      const s = document.createElement('script');
      s.src = 'https://docs.opencv.org/4.8.0/opencv.js';
      s.async = true;
      s.onerror = () => reject(new Error('Failed to load OpenCV'));
      s.onload = () => {
        if (window.cv?.Mat) { resolve(); return; }
        const check = setInterval(() => { if (window.cv?.Mat) { clearInterval(check); resolve(); } }, 100);
        setTimeout(() => { clearInterval(check); reject(new Error('cv init timeout')); }, 20000);
      };
      document.head.appendChild(s);
    });
  }
  if (!window.jscanify) {
    await new Promise<void>((resolve, reject) => {
      const s = document.createElement('script');
      s.src = 'https://cdn.jsdelivr.net/npm/jscanify@0.1.3/src/jscanify.js';
      s.onload = () => resolve();
      s.onerror = () => reject(new Error('Failed to load jscanify'));
      document.head.appendChild(s);
    });
  }
  if (!window.jscanify) throw new Error('jscanify not available');
  return new window.jscanify();
}

// ─── Image enhancement ───────────────────────────────────────────────────────

function enhance(imageData: ImageData): ImageData {
  const d = imageData.data;
  const c = 1.8, b = 20;
  for (let i = 0; i < d.length; i += 4) {
    d[i]   = Math.min(255, Math.max(0, (d[i]   - 128) * c + 128 + b));
    d[i+1] = Math.min(255, Math.max(0, (d[i+1] - 128) * c + 128 + b));
    d[i+2] = Math.min(255, Math.max(0, (d[i+2] - 128) * c + 128 + b));
  }
  return imageData;
}

// ─── Overlay drawing ─────────────────────────────────────────────────────────

/** Map a video-pixel coordinate to a CSS-pixel coordinate for object-cover rendering */
function toCSS(vx: number, vy: number, vw: number, vh: number, cw: number, ch: number): Pt {
  const s = Math.max(cw / vw, ch / vh);
  return { x: vx * s + (cw - vw * s) / 2, y: vy * s + (ch - vh * s) / 2 };
}

function roundedRectPath(ctx: CanvasRenderingContext2D, x: number, y: number, w: number, h: number, r: number) {
  ctx.beginPath();
  ctx.moveTo(x + r, y);
  ctx.lineTo(x + w - r, y);
  ctx.quadraticCurveTo(x + w, y, x + w, y + r);
  ctx.lineTo(x + w, y + h - r);
  ctx.quadraticCurveTo(x + w, y + h, x + w - r, y + h);
  ctx.lineTo(x + r, y + h);
  ctx.quadraticCurveTo(x, y + h, x, y + h - r);
  ctx.lineTo(x, y + r);
  ctx.quadraticCurveTo(x, y, x + r, y);
  ctx.closePath();
}

/** Document overlay: darkens outside, clears inside, primary border + corner dots. */
function drawDocOverlay(ctx: CanvasRenderingContext2D, q: Quad, cw: number, ch: number) {
  const { tl, tr, br, bl } = q;
  const primary = appHsl('--primary');

  ctx.fillStyle = 'rgba(0,0,0,0.55)';
  ctx.fillRect(0, 0, cw, ch);

  ctx.globalCompositeOperation = 'destination-out';
  ctx.beginPath();
  ctx.moveTo(tl.x, tl.y);
  ctx.lineTo(tr.x, tr.y);
  ctx.lineTo(br.x, br.y);
  ctx.lineTo(bl.x, bl.y);
  ctx.closePath();
  ctx.fill();
  ctx.globalCompositeOperation = 'source-over';

  ctx.strokeStyle = primary;
  ctx.lineWidth = 2.5;
  ctx.beginPath();
  ctx.moveTo(tl.x, tl.y);
  ctx.lineTo(tr.x, tr.y);
  ctx.lineTo(br.x, br.y);
  ctx.lineTo(bl.x, bl.y);
  ctx.closePath();
  ctx.stroke();

  for (const pt of [tl, tr, br, bl]) {
    ctx.fillStyle = primary;
    ctx.beginPath(); ctx.arc(pt.x, pt.y, 7, 0, Math.PI * 2); ctx.fill();
    ctx.fillStyle = 'white';
    ctx.beginPath(); ctx.arc(pt.x, pt.y, 3.5, 0, Math.PI * 2); ctx.fill();
  }
}

/** Dashed white guide frame shown when no document is detected */
function drawGuideFrame(ctx: CanvasRenderingContext2D, cw: number, ch: number) {
  const mx = cw * 0.05, my = ch * 0.10;
  const gw = cw - mx * 2, gh = ch - my * 2;
  const r = 14;

  ctx.fillStyle = 'rgba(0,0,0,0.42)';
  ctx.fillRect(0, 0, cw, ch);

  ctx.globalCompositeOperation = 'destination-out';
  roundedRectPath(ctx, mx, my, gw, gh, r);
  ctx.fill();
  ctx.globalCompositeOperation = 'source-over';

  ctx.strokeStyle = 'rgba(255,255,255,0.5)';
  ctx.lineWidth = 1.5;
  ctx.setLineDash([12, 7]);
  roundedRectPath(ctx, mx, my, gw, gh, r);
  ctx.stroke();
  ctx.setLineDash([]);
}

// ─── Component ───────────────────────────────────────────────────────────────

export default function DocumentScanner({ onCapture, onClose }: Props) {
  const videoRef    = useRef<HTMLVideoElement>(null);
  const overlayRef  = useRef<HTMLCanvasElement>(null);
  const streamRef   = useRef<MediaStream | null>(null);
  const animRef     = useRef<number>(0);
  const scannerRef  = useRef<any>(null);
  const cornersRef    = useRef<Quad | null>(null); // CSS-pixel coords (for overlay)
  const rawCornersRef = useRef<Quad | null>(null); // video-pixel coords (for extraction)
  const detectedRef   = useRef(false);
  const detectTsRef = useRef(0);

  const [capturedUrl, setCapturedUrl]     = useState<string | null>(null);
  const [cameraReady, setCameraReady]     = useState(false);
  const [scannerReady, setScannerReady]   = useState(false);
  const [detected, setDetected]           = useState(false);

  useEffect(() => {
    let cancelled = false;

    async function start() {
      const constraints: MediaStreamConstraints[] = [
        { video: { facingMode: { ideal: 'environment' }, width: { ideal: 1920 }, height: { ideal: 1080 } } },
        { video: { facingMode: 'environment' } },
        { video: true },
      ];
      let stream: MediaStream | null = null;
      for (const c of constraints) {
        try { stream = await navigator.mediaDevices.getUserMedia(c); break; } catch {}
      }
      if (!stream || cancelled) return;
      streamRef.current = stream;
      if (videoRef.current) {
        videoRef.current.srcObject = stream;
        await videoRef.current.play().catch(() => {});
      }
      setCameraReady(true);

      try {
        const s = await loadScannerLibs();
        if (cancelled) return;
        scannerRef.current = s;
        setScannerReady(true);
      } catch { /* scanner optional — falls back to guide frame */ }

      if (!cancelled) startDetection();
    }

    start();
    return () => { cancelled = true; stopCamera(); };
  }, []);

  function startDetection() {
    cancelAnimationFrame(animRef.current);

    function detect(ts: DOMHighResTimeStamp) {
      const video   = videoRef.current;
      const overlay = overlayRef.current;
      if (!video || !overlay) return;

      // Sync canvas intrinsic size to CSS size (1:1 CSS-pixel mapping)
      const cw = overlay.offsetWidth;
      const ch = overlay.offsetHeight;
      if (cw === 0 || ch === 0) { animRef.current = requestAnimationFrame(detect); return; }
      if (overlay.width !== cw || overlay.height !== ch) { overlay.width = cw; overlay.height = ch; }

      const ctx = overlay.getContext('2d')!;
      ctx.clearRect(0, 0, cw, ch);

      // Throttle heavy OpenCV detection to ~8 fps
      const scanner = scannerRef.current;
      if (scanner && video.readyState >= 2 && ts - detectTsRef.current > 120) {
        detectTsRef.current = ts;
        try {
          const contour = scanner.findPaperContour(video);
          if (contour && contour.rows >= 4) {
            const vw = video.videoWidth || 1280;
            const vh = video.videoHeight || 720;
            const pts: Pt[] = [];
            for (let i = 0; i < Math.min(contour.rows, 4); i++) {
              pts.push({ x: contour.data32S[i * 2], y: contour.data32S[i * 2 + 1] });
            }
            // Sort into tl/tr/br/bl using sum/difference method
            const tl = pts.reduce((a, b) => a.x + a.y < b.x + b.y ? a : b);
            const br = pts.reduce((a, b) => a.x + a.y > b.x + b.y ? a : b);
            const tr = pts.reduce((a, b) => a.x - a.y > b.x - b.y ? a : b);
            const bl = pts.reduce((a, b) => a.x - a.y < b.x - b.y ? a : b);
            rawCornersRef.current = { tl, tr, br, bl }; // keep video-pixel coords for extractPaper
            cornersRef.current = {
              tl: toCSS(tl.x, tl.y, vw, vh, cw, ch),
              tr: toCSS(tr.x, tr.y, vw, vh, cw, ch),
              br: toCSS(br.x, br.y, vw, vh, cw, ch),
              bl: toCSS(bl.x, bl.y, vw, vh, cw, ch),
            };
            if (!detectedRef.current) { detectedRef.current = true; setDetected(true); }
          } else {
            rawCornersRef.current = null;
            cornersRef.current = null;
            if (detectedRef.current) { detectedRef.current = false; setDetected(false); }
          }
        } catch {
          cornersRef.current = null;
        }
      }

      // Draw at full 60 fps using cached result
      if (cornersRef.current) {
        drawDocOverlay(ctx, cornersRef.current, cw, ch);
      } else {
        drawGuideFrame(ctx, cw, ch);
      }

      animRef.current = requestAnimationFrame(detect);
    }

    animRef.current = requestAnimationFrame(detect);
  }

  function capture() {
    const video = videoRef.current;
    if (!video) return;
    cancelAnimationFrame(animRef.current);

    const overlay = overlayRef.current;
    if (overlay) overlay.getContext('2d')?.clearRect(0, 0, overlay.width, overlay.height);

    const d2 = (a: Pt, b: Pt) => Math.sqrt((a.x - b.x) ** 2 + (a.y - b.y) ** 2);
    const scanner = scannerRef.current;
    const raw = rawCornersRef.current;
    let canvas: HTMLCanvasElement | null = null;

    if (scanner && raw) {
      // Compute output size from the actual document edge lengths so aspect ratio is preserved
      const outW = Math.max(400, Math.round((d2(raw.tl, raw.tr) + d2(raw.bl, raw.br)) / 2));
      const outH = Math.max(400, Math.round((d2(raw.tl, raw.bl) + d2(raw.tr, raw.br)) / 2));
      try { canvas = scanner.extractPaper(video, outW, outH); } catch {}
    } else if (scanner) {
      // Scanner loaded but no corners yet — still try extractPaper
      try { canvas = scanner.extractPaper(video, video.videoWidth || 1280, video.videoHeight || 720); } catch {}
    }

    // Fallback: crop to detected bounding box instead of dumping the full frame
    if ((!canvas || canvas.width === 0) && raw) {
      const xs = [raw.tl.x, raw.tr.x, raw.br.x, raw.bl.x];
      const ys = [raw.tl.y, raw.tr.y, raw.br.y, raw.bl.y];
      const x0 = Math.max(0, Math.min(...xs));
      const y0 = Math.max(0, Math.min(...ys));
      const cw = Math.min(video.videoWidth, Math.max(...xs)) - x0;
      const ch = Math.min(video.videoHeight, Math.max(...ys)) - y0;
      canvas = document.createElement('canvas');
      canvas.width  = cw;
      canvas.height = ch;
      canvas.getContext('2d')!.drawImage(video, x0, y0, cw, ch, 0, 0, cw, ch);
    }

    // Last resort: full frame
    if (!canvas || canvas.width === 0) {
      canvas = document.createElement('canvas');
      canvas.width  = video.videoWidth  || video.offsetWidth  || 1280;
      canvas.height = video.videoHeight || video.offsetHeight || 720;
      canvas.getContext('2d')!.drawImage(video, 0, 0, canvas.width, canvas.height);
    }

    const ctx = canvas.getContext('2d')!;
    ctx.putImageData(enhance(ctx.getImageData(0, 0, canvas.width, canvas.height)), 0, 0);
    setCapturedUrl(canvas.toDataURL('image/jpeg', 0.92));
  }

  function retake() {
    setCapturedUrl(null);
    cornersRef.current    = null;
    rawCornersRef.current = null;
    detectedRef.current   = false;
    setDetected(false);
    startDetection();
  }

  function stopCamera() {
    cancelAnimationFrame(animRef.current);
    streamRef.current?.getTracks().forEach(t => t.stop());
    if (videoRef.current) videoRef.current.srcObject = null;
  }

  // Status text
  const statusText = capturedUrl
    ? 'Review scan'
    : !cameraReady
    ? 'Starting camera…'
    : !scannerReady
    ? 'Loading scanner…'
    : detected
    ? 'Document detected'
    : 'Position document in frame';

  return (
    <div className="fixed inset-0 z-[200] bg-black select-none touch-manipulation">

      {/* ── Camera feed ───────────────────────────────────── */}
      <video
        ref={videoRef}
        autoPlay
        playsInline
        muted
        className="absolute inset-0 h-full w-full object-cover"
      />

      {/* ── Detection overlay ─────────────────────────────── */}
      {!capturedUrl && (
        <canvas
          ref={overlayRef}
          className="absolute inset-0 w-full h-full"
          style={{ pointerEvents: 'none' }}
        />
      )}

      {/* ── Captured preview ──────────────────────────────── */}
      {capturedUrl && (
        <div className="absolute inset-0 bg-black flex items-center justify-center">
          <img
            src={capturedUrl}
            alt="Captured"
            className="max-h-full max-w-full object-contain"
          />
        </div>
      )}

      {/* ── Top bar ───────────────────────────────────────── */}
      <div className="absolute top-0 left-0 right-0 flex items-center justify-between px-5 pt-12 pb-3 pointer-events-none"
           style={{ background: 'linear-gradient(to bottom, rgba(0,0,0,0.6) 0%, transparent 100%)' }}>
        <button
          type="button"
          onClick={() => { stopCamera(); onClose(); }}
          className="pointer-events-auto text-primary-foreground font-medium text-[15px] leading-none px-4 py-2 rounded-full backdrop-blur-sm"
          style={{ background: 'rgba(0,0,0,0.35)' }}
        >
          Cancel
        </button>
        {/* placeholder for flash button */}
        <div className="w-16" />
      </div>

      {/* ── Status badge ──────────────────────────────────── */}
      <div className="absolute left-0 right-0 flex justify-center pointer-events-none" style={{ top: '11%' }}>
        <div
          className="px-4 py-[6px] rounded-full text-primary-foreground text-[13px] font-medium backdrop-blur-sm transition-colors duration-300"
          style={{ background: detected && !capturedUrl ? 'hsl(var(--primary) / 0.85)' : 'rgba(0,0,0,0.50)' }}
        >
          {statusText}
        </div>
      </div>

      {/* ── Bottom controls ───────────────────────────────── */}
      <div
        className="absolute bottom-0 left-0 right-0 flex flex-col items-center pb-12 pt-8"
        style={{ background: 'linear-gradient(to top, rgba(0,0,0,0.72) 0%, transparent 100%)' }}
      >
        {!capturedUrl ? (
          /* Shutter */
          <button
            type="button"
            onClick={capture}
            disabled={!cameraReady}
            className="w-[76px] h-[76px] rounded-full flex items-center justify-center disabled:opacity-40 active:scale-95 transition-transform"
            style={{ border: '3px solid white' }}
          >
            <span
              className="block rounded-full"
              style={{ width: 58, height: 58, background: 'white' }}
            />
          </button>
        ) : (
          /* Keep / Retake */
          <div className="w-full flex items-center justify-between px-10">
            <button
              type="button"
              onClick={retake}
            className="text-primary-foreground font-medium text-[17px] px-5 py-2 rounded-full backdrop-blur-sm"
              style={{ background: 'rgba(0,0,0,0.35)' }}
            >
              Retake
            </button>
            <button
              type="button"
              onClick={() => { if (capturedUrl) onCapture(capturedUrl); }}
              className="text-primary-foreground font-semibold text-[17px] px-6 py-2 rounded-full"
              style={{ background: 'hsl(var(--primary))' }}
            >
              Keep Scan
            </button>
          </div>
        )}
      </div>

    </div>
  );
}
